import React from 'react';
import {
  CaregivingIcon,
  FaithIcon,
  FinancesIcon,
  GriefIcon,
  IdentityIcon,
  MedicalIcon,
  OverwhelmIcon,
  RelationshipsIcon,
  SchoolIcon,
  WorkIcon,
} from './icons/topic-icons/topic-icons';
import { getTopicShortDescriptionByName } from '../utils/topic-utils';
import { TagGroup } from '../hooks/api/useTagGroups';

// Map tag group keys to icon components
const TOPIC_ICON_COMPONENT_MAP: Record<string, React.FC<{ isSelected?: boolean }>> = {
  RELATIONSHIP_ISSUES: RelationshipsIcon,
  GRIEF_AND_LOSS: GriefIcon,
  IDENTITY_AND_BELONGING: IdentityIcon,
  WORK_STRESS: WorkIcon,
  FINANCIAL_STRESS: FinancesIcon,
  SCHOOL_STRESS: SchoolIcon,
  MEDICAL_CONDITIONS: MedicalIcon,
  OVERWHELMED_AND_COPING: OverwhelmIcon,
  CAREGIVING: CaregivingIcon,
  FAITH: FaithIcon,
};

const getTopicIcon = (tagGroupKey: string | undefined): React.FC<{ isSelected?: boolean }> | null => {
  if (!tagGroupKey) return null;
  return TOPIC_ICON_COMPONENT_MAP[tagGroupKey] || null;
};

export interface TopicSelectionContentProps {
  topicTagGroups: TagGroup[];
  isLoading: boolean;
  selectedTagGroupIds: number[];
  onToggleTagGroup: (tagGroupId: number) => void;
  maxSelections?: number;
  useShortDescription?: boolean;
  layout?: 'grid' | 'rows';
  toastNotificationContainerRef?: React.RefObject<HTMLDivElement>;
}

export const TopicSelectionContent: React.FC<TopicSelectionContentProps> = ({
  topicTagGroups,
  isLoading,
  selectedTagGroupIds,
  onToggleTagGroup,
  maxSelections = 4,
  useShortDescription = false,
  layout = 'grid',
  toastNotificationContainerRef,
}) => {
  if (isLoading) {
    return (
      <div className="py-8 text-center">
        <p className="font-open-sans text-sm font-normal text-gray-500">Loading topics...</p>
      </div>
    );
  }

  if (topicTagGroups.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="font-open-sans text-sm font-normal text-gray-500">No topics available.</p>
      </div>
    );
  }

  const renderTopicCard = (group: TagGroup, widthClass?: string) => {
    const isSelected = selectedTagGroupIds.includes(group.id);
    const IconComponent = getTopicIcon(group.key);
    const description = useShortDescription
      ? getTopicShortDescriptionByName(group.name, topicTagGroups)
      : group.description;

    return (
      <button
        key={group.id}
        type="button"
        onClick={() => onToggleTagGroup(group.id)}
        className={`flex ${widthClass ?? 'w-full'} flex-col items-start gap-2 rounded-2xl px-3 py-4 text-left transition ${
          isSelected ? 'bg-see-more' : 'bg-button-secondary hover:bg-connect-bg'
        }`}
      >
        <div className="flex size-[36px] shrink-0 items-center justify-center rounded-lg bg-white">
          {IconComponent ? <IconComponent isSelected={isSelected} /> : null}
        </div>
        <div className="flex w-full flex-col items-start leading-[1.5]">
          <p className="font-albert-sans text-sm font-semibold text-black">{group.name}</p>
          {description && <p className="font-open-sans text-muted-text-gray mt-0 text-xs font-normal">{description}</p>}
        </div>
      </button>
    );
  };

  return (
    <>
      {toastNotificationContainerRef && <div ref={toastNotificationContainerRef} />}
      {layout === 'grid' ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {topicTagGroups.map((group) => renderTopicCard(group))}
        </div>
      ) : (
        <div className="flex w-full flex-col items-center gap-6">
          {Array.from({ length: Math.ceil(topicTagGroups.length / 2) }).map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="flex w-full flex-col items-stretch gap-6 md:w-auto md:flex-row md:items-start md:gap-6"
            >
              {topicTagGroups
                .slice(rowIndex * 2, rowIndex * 2 + 2)
                .map((group) => renderTopicCard(group, 'w-full md:w-[303px]'))}
              {/* Fill empty space if odd number of items */}
              {topicTagGroups.slice(rowIndex * 2, rowIndex * 2 + 2).length === 1 && (
                <div className="hidden w-[303px] md:block" aria-hidden />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
